import { useMutation } from "@apollo/client";
import { StackScreenProps } from "@react-navigation/stack";
import {
  Button,
  Card,
  Divider,
  FlatActionList,
  Page,
  Stack,
  Text,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import { StockOrder } from "@towersystems/roam-common/lib/generated-types";
import React from "react";
import { useFormContext } from "react-hook-form";
import { Colors } from "react-native-ui-lib";
import { SupplierIdToName } from "../../../../components";
import { NonMatchingProductBanner } from "../../../../components/NonMatchingProductBanner";
import { AddProductOrderItemRootScreenParamList } from "../../../../types";
import ProductModel from "../../../../utilities/data/model/Product";
import { useTranslation } from "../../../../utilities/i18n";
import { ProductSummaryCard } from "../../../products/components";
import { MatchingBaseProductBanner } from "../../components/MatchingBaseProductBanner";
import { OrderItemForm } from "../../components/OrderItemForm";
import { OrderItemFormValues } from "../../components/OrderItemForm/types";
import { MUTATION_UPDATE_STOCK_ORDER_LINE_ITEMS } from "../../utils";
import { useStockOrderContextProvider } from "../../utils/active-stock-order";
import { textButtonProps } from "../common";
import { useAddOrderItemScreenActionsProvider } from "./hooks";

export type AddOrderItemScreenProps = StackScreenProps<
  AddProductOrderItemRootScreenParamList,
  "AddOrEditOrderItemScreen"
>;

export const AddOrEditOrderItem = ({
  navigation,
  route,
}: AddOrderItemScreenProps & {
  product?: ProductModel;
}) => {
  const { baseProductMatchNotice } = route.params ?? {};

  const { handleSubmit, formState, watch, setError, trigger } =
    useFormContext<OrderItemFormValues>();

  const [m] = useMutation<{ updateStockOrderLineItems: StockOrder }>(
    MUTATION_UPDATE_STOCK_ORDER_LINE_ITEMS
  );

  const { t } = useTranslation("stockorder");

  const maybePageContext = useAddOrderItemScreenActionsProvider();

  const {
    errors,
    isDirty,
    isLoading,
    isSubmitting,
    isValid,
    isSubmitSuccessful,
  } = formState;

  const flashMessagesContext = useFlashMessages();
  const watchedStockOrderId = watch("stockOrderId");
  const watchedSupplierId = watch("supplierId");
  const watchedId = watch("id");
  const watchedProductId = watch("productId");

  React.useEffect(() => {
    if (watchedId) {
      navigation.setOptions({
        headerRight: () => {
          return (
            <Button
              label={t("button.remove_line_item")}
              isDestructive
              disabled={false}
              onPress={handleOnRemoveLineItem}
              size="compact"
            ></Button>
          );
        },
      });
    }
  }, []);

  const handleOnRemoveLineItem = React.useCallback(async () => {
    if (watchedStockOrderId && watchedId) {
      try {
        const result = await m({
          variables: {
            id: watchedStockOrderId,
            itemsToRemove: [watchedId],
          },
        });

        if (result.data?.updateStockOrderLineItems) {
          setStockOrder(result.data?.updateStockOrderLineItems);
        }

        navigation.goBack();
      } catch (err: any) {
        flashMessagesContext.showMessage({
          message: err.message ?? t("common:error"),
          severity: "error",
        });
        setError("root.serverError", {
          type: "400",
          message: err.message ?? t("common:error"),
        });
      }
    }
  }, [watchedStockOrderId, watchedId]);

  React.useEffect(() => {
    // to reset the form if the api returns error
    if (errors.root?.serverError) {
      trigger();
    }
  }, [errors.root?.serverError]);

  const { stockOrder, setStockOrder } = useStockOrderContextProvider();

  const handleOnSubmit = React.useCallback(
    async (values: OrderItemFormValues) => {
      await m({
        variables: {
          id: values.stockOrderId,
          itemsToCreate: !values.id
            ? [
                {
                  productId: values.productId,
                  quantity: parseFloat(values.quantity),
                  supplierId: values.supplierId ?? null,
                  supplierCode: values.supplierCode ?? null,
                  name: values.name,
                  barcode: values.barcode,
                },
              ]
            : [],
          itemsToUpdate: values.id
            ? [
                {
                  id: values.id,
                  quantity: parseFloat(values.quantity),
                  supplierId: values.supplierId?.length
                    ? values.supplierId
                    : null,
                  supplierCode: values.supplierCode ?? null,
                },
              ]
            : [],
        },
      })
        .then((res) => {
          if (res.data?.updateStockOrderLineItems) {
            setStockOrder(res.data?.updateStockOrderLineItems);
            maybePageContext &&
              maybePageContext.afterSubmitSuccess(
                res.data?.updateStockOrderLineItems
              );
          } else {
            throw new Error(t("common:error"));
          }
        })
        .catch((err) => {
          flashMessagesContext.showMessage({
            message: err.message ?? t("common:error"),
            severity: "error",
          });
          setError("root.serverError", {
            type: "400",
            message: t("common:error"),
          });
        });
    },
    []
  );

  const disabled =
    !isDirty || isLoading || isSubmitting || !isValid || isSubmitSuccessful;

  const footerMarkup = (
    <Button
      disabled={disabled}
      label={
        watchedId ? t("button.save_order_item") : t("button.add_order_item")
      }
      backgroundColor={Colors.action}
      color="white"
      onPress={handleSubmit(handleOnSubmit)}
    ></Button>
  );

  const title = watchedId ? t("edit_item_title") : t("add_item_title");

  const baseProductNotice = baseProductMatchNotice ? (
    <MatchingBaseProductBanner />
  ) : undefined;

  const nonMatchingProductNotice =
    !baseProductMatchNotice && !watchedProductId ? (
      <NonMatchingProductBanner />
    ) : undefined;

  return (
    <Page title={title} footer={footerMarkup}>
      {watchedProductId ? (
        <>
          <ProductSummaryCard
            productId={watchedProductId}
            locationId={stockOrder.locationId}
            onPress={() => {
              navigation.navigate("EditProductRootScreen", {
                productId: watchedProductId,
                screen: "EditProductScreen",
              });
            }}
          />
          <Divider />
        </>
      ) : null}
      <Stack vertical>
        {baseProductNotice}
        {nonMatchingProductNotice}
        <Card title={t("card.order_details.title")}>
          <Stack vertical>
            <OrderItemForm.QuantityFormInput />
          </Stack>
        </Card>
        <Card title={t("card.product_details.title")}>
          <Stack vertical>
            <OrderItemForm.NameFormInput />
            <OrderItemForm.BarcodeFormInput />
            <Divider marginVertical={0} />
            <FlatActionList
              items={[
                {
                  key: "supplierId",
                  title: t("form.supplier.label"),
                  subTitle: t("form.supplier.description"),
                  onPress: () =>
                    navigation.navigate("ProductSelectSupplierScreen"),
                  componentRight: () =>
                    watchedSupplierId ? (
                      <SupplierIdToName
                        supplierId={watchedSupplierId}
                        {...(textButtonProps as any)}
                      />
                    ) : (
                      <Text {...(textButtonProps as any)}>Select Supplier</Text>
                    ),
                },
              ]}
            />
            {watchedSupplierId ? (
              <>
                <OrderItemForm.SupplierCodeFormInput />
              </>
            ) : null}
          </Stack>
        </Card>
      </Stack>
    </Page>
  );
};

export const AddOrEditOrderItemScreen = (props: AddOrderItemScreenProps) => {
  return <AddOrEditOrderItem {...props} />;
};
